import { FeatureFlags } from '@jms/model';

/**
 * A legacy screen that a v2 entity replaces. The entity manifest never knows about legacy; this list
 * is the only place that does, and an entry is deleted with the legacy code it describes.
 */
export interface LegacyEntityRoute {
    /** v2 manifest slug the legacy screen redirects to. */
    slug: string;
    /** Legacy list URLs, also the side-nav paths that hide when legacy is off. Some screens are mounted in two menus. */
    paths: readonly string[];
    /** Legacy record URL after a list path, with `:id` marking the record id. Absent when legacy has no record page. */
    record?: string;
    /** Tenant flag that turns the legacy screen off. Only honoured while the v2 entity is on. */
    disabledFlag: FeatureFlags;
}

export const LEGACY_ENTITY_ROUTES: readonly LegacyEntityRoute[] = [
    { slug: 'invoices', paths: ['/finance/invoices'], record: ':id', disabledFlag: FeatureFlags.FinanceDocsInvoiceLegacyDisabled },
    { slug: 'bills', paths: ['/finance/bills'], record: ':id', disabledFlag: FeatureFlags.FinanceDocsBillLegacyDisabled },
    { slug: 'purchase-orders', paths: ['/finance/purchase-orders'], record: 'detail/:id', disabledFlag: FeatureFlags.FinanceDocsPurchaseOrderLegacyDisabled },
    { slug: 'sales-orders', paths: ['/finance/sales-order'], disabledFlag: FeatureFlags.FinanceDocsSalesOrderLegacyDisabled },
    { slug: 'payments', paths: ['/finance/accounts-receivable'], disabledFlag: FeatureFlags.FinanceDocsPaymentLegacyDisabled },
    { slug: 'quotes', paths: ['/quotes/quotes-new', '/sales/quotes-new'], record: 'manage/:id', disabledFlag: FeatureFlags.FinanceDocsQuoteLegacyDisabled },
    { slug: 'quote-templates', paths: ['/quotes/quote-templates', '/sales/quote-templates'], disabledFlag: FeatureFlags.FinanceDocsQuoteTemplateLegacyDisabled },
];

export function findLegacyEntityRoute(slug: string): LegacyEntityRoute | undefined {

    return LEGACY_ENTITY_ROUTES.find(entry => entry.slug === slug);
}

/**
 * Legacy is off only while v2 is on: turning legacy off without its replacement would leave the
 * tenant with no screen at all, so that combination keeps legacy.
 */
export function isLegacyEntityDisabled(entry: LegacyEntityRoute, flags: Partial<Record<FeatureFlags, boolean>> | undefined, v2Enabled: boolean): boolean {

    return v2Enabled && flags?.[entry.disabledFlag] === true;
}

/** Record id in a legacy URL, or null when the URL is the list or another child page. */
export function legacyRecordId(entry: LegacyEntityRoute, url: string): string | null {

    if (!entry.record) return null;

    const path = url.split(/[?#;(]/)[0];
    const base = entry.paths.find(candidate => path === candidate || path.startsWith(`${candidate}/`));
    if (!base) return null;

    const rest = path.slice(base.length).replace(/^\/+/, '');
    const actual = rest.split('/');
    const expected = entry.record.split('/');
    if (actual.length !== expected.length) return null;

    let id: string | null = null;
    for (let i = 0; i < expected.length; i++) {
        if (expected[i] === ':id') id = decodeURIComponent(actual[i]);
        else if (expected[i] !== actual[i]) return null;
    }
    return id || null;
}
